import { useEffect, useMemo, useState } from 'react';
import { CheckCircle, Download, KeyRound, Loader2, Power, Trash2, X } from 'lucide-react';
import type { Guardian, GuardianLink } from 'shared';
import {
  useActivateGuardian,
  useDeactivateGuardian,
  useDeleteGuardian,
  useGuardian,
  useLinkEducando,
  useUnlinkEducando,
} from '@/hooks/useGuardians';
import { useDataExport } from '@/hooks/useDataExport';
import { apiErrorCode } from '@/lib/api';
import { formatDay } from '@/lib/guardianView';
import { ConfirmDialog, ErrorBlock, LoadingBlock } from '@/components/activities/ActivityUi';
import ResetLinkDialog from '@/components/ResetLinkDialog';
import { GuardianEstadoBadge } from './GuardianEstadoBadge';
import { EducandosSection, ParticipantLinkSearch, SuggestionsSection } from './GuardianLinkSections';

interface GuardianDetailModalProps {
  guardianId: number;
  onClose: () => void;
  onNotice: (message: string, variant: 'success' | 'error') => void;
}

type Confirm =
  | { type: 'none' }
  | { type: 'delete' }
  | { type: 'deactivate' }
  | { type: 'resetLink' }
  | { type: 'unlink'; link: GuardianLink };

const actionButton =
  'flex items-center gap-1.5 px-3 py-2 rounded-md text-sm font-medium disabled:opacity-50 transition-colors';

function fullName(g: Guardian): string {
  return `${g.nome} ${g.apelido}`;
}

/** A guardian's account, with what staff may do to it and to its links. */
export default function GuardianDetailModal({ guardianId, onClose, onNotice }: GuardianDetailModalProps) {
  const { data, isLoading, isError, refetch } = useGuardian(guardianId);
  const activate = useActivateGuardian();
  const deactivate = useDeactivateGuardian();
  const remove = useDeleteGuardian();
  const link = useLinkEducando();
  const unlink = useUnlinkEducando();
  const dataExport = useDataExport();
  const [confirm, setConfirm] = useState<Confirm>({ type: 'none' });

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && confirm.type === 'none') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [confirm.type, onClose]);

  const linkedIds = useMemo(
    () => new Set((data?.educandos ?? []).map((l) => l.participantId)),
    [data?.educandos],
  );
  const linkingId = link.isPending ? (link.variables?.participantId ?? null) : null;

  const guardian = data?.guardian;
  const name = guardian ? fullName(guardian) : '';

  const handleActivate = async () => {
    try {
      await activate.mutateAsync(guardianId);
      onNotice(`Conta de "${name}" ativa. Já pode entrar na app.`, 'success');
    } catch {
      onNotice('Não foi possível ativar a conta.', 'error');
    }
  };

  const handleDeactivate = async () => {
    try {
      await deactivate.mutateAsync(guardianId);
      onNotice(`Conta de "${name}" desativada.`, 'success');
    } catch {
      onNotice('Não foi possível desativar a conta.', 'error');
    }
    setConfirm({ type: 'none' });
  };

  const handleDelete = async () => {
    try {
      await remove.mutateAsync(guardianId);
      onNotice(`Conta de "${name}" eliminada.`, 'success');
      onClose();
    } catch {
      onNotice('Não foi possível eliminar a conta.', 'error');
      setConfirm({ type: 'none' });
    }
  };

  const handleExport = async () => {
    try {
      await dataExport.mutateAsync({ subject: 'guardian', id: guardianId });
      onNotice(`Dados de "${name}" exportados. O ficheiro está nas transferências.`, 'success');
    } catch {
      onNotice('Não foi possível exportar os dados.', 'error');
    }
  };

  const handleLink = async (participantId: number) => {
    try {
      await link.mutateAsync({ guardianId, participantId });
      onNotice('Educando associado.', 'success');
    } catch (err) {
      if (apiErrorCode(err) === 'ALREADY_LINKED') {
        onNotice('Este participante já está associado.', 'error');
        void refetch();
        return;
      }
      onNotice('Não foi possível associar o participante.', 'error');
    }
  };

  const handleUnlink = async (target: GuardianLink) => {
    try {
      await unlink.mutateAsync({ guardianId, participantId: target.participantId });
      onNotice(`Associação de "${target.participantName}" removida.`, 'success');
    } catch {
      onNotice('Não foi possível remover a associação.', 'error');
    }
    setConfirm({ type: 'none' });
  };

  return (
    <div
      className="fixed inset-0 z-40 flex items-start justify-center bg-black/50 p-4 overflow-y-auto"
      role="dialog"
      aria-modal="true"
      aria-labelledby="guardian-detail-title"
    >
      <div className="bg-card border border-border rounded-xl shadow-2xl w-full max-w-2xl my-8">
        <div className="flex items-start justify-between gap-4 p-6 border-b border-border">
          <div className="min-w-0">
            <h2 id="guardian-detail-title" className="text-lg font-semibold text-foreground truncate">
              {guardian ? name : 'Encarregado de Educação'}
            </h2>
            {guardian && (
              <div className="mt-1 flex items-center gap-2 flex-wrap">
                <GuardianEstadoBadge estado={guardian.estado} />
                <span className="text-xs text-muted-foreground">
                  Registo a {formatDay(guardian.createdAt)}
                  {guardian.approvedAt && guardian.approvedByName
                    ? ` · aprovado a ${formatDay(guardian.approvedAt)} por ${guardian.approvedByName}`
                    : ''}
                </span>
              </div>
            )}
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Fechar"
            className="p-1.5 rounded-md text-muted-foreground hover:bg-accent hover:text-foreground transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {isLoading && <LoadingBlock />}
        {isError && <ErrorBlock message="Não foi possível carregar o Encarregado de Educação." />}

        {data && guardian && (
          <div className="p-6 space-y-6">
            <dl className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
              <div>
                <dt className="text-xs text-muted-foreground">Email</dt>
                <dd className="text-foreground break-all">{guardian.email}</dd>
              </div>
              <div>
                <dt className="text-xs text-muted-foreground">Telemóvel</dt>
                <dd className="text-foreground">{guardian.telemovel}</dd>
              </div>
            </dl>

            <div className="flex flex-wrap gap-2">
              {guardian.estado !== 'Ativo' && (
                <button
                  type="button"
                  onClick={handleActivate}
                  disabled={activate.isPending}
                  className={`${actionButton} bg-primary text-primary-foreground hover:bg-primary/90`}
                >
                  {activate.isPending ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <CheckCircle className="w-4 h-4" />
                  )}
                  {guardian.estado === 'Pendente' ? 'Aprovar' : 'Reativar'}
                </button>
              )}
              {guardian.estado === 'Ativo' && (
                <button
                  type="button"
                  onClick={() => setConfirm({ type: 'deactivate' })}
                  className={`${actionButton} border border-border text-foreground hover:bg-accent`}
                >
                  <Power className="w-4 h-4" />
                  Desativar
                </button>
              )}
              {guardian.estado === 'Ativo' && (
                <button
                  type="button"
                  onClick={() => setConfirm({ type: 'resetLink' })}
                  title="Uma ligação para definir uma nova palavra-passe, para entregares à pessoa"
                  className={`${actionButton} border border-border text-foreground hover:bg-accent`}
                >
                  <KeyRound className="w-4 h-4" />
                  Recuperar palavra-passe
                </button>
              )}
              <button
                type="button"
                onClick={handleExport}
                disabled={dataExport.isPending}
                title="Tudo o que a app guarda sobre esta pessoa, num ficheiro, para um pedido de acesso (RGPD)"
                className={`${actionButton} border border-border text-foreground hover:bg-accent`}
              >
                {dataExport.isPending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
                Exportar dados
              </button>
              <button
                type="button"
                onClick={() => setConfirm({ type: 'delete' })}
                className={`${actionButton} border border-border text-destructive hover:bg-destructive/10`}
              >
                <Trash2 className="w-4 h-4" />
                {guardian.estado === 'Pendente' ? 'Recusar' : 'Eliminar'}
              </button>
            </div>

            {guardian.estado === 'Pendente' && (
              <p className="text-sm rounded-md bg-yellow-100 text-yellow-800 dark:bg-yellow-900/20 dark:text-yellow-300 px-3 py-2" role="note">
                Esta pessoa ainda não pode entrar na app. Confirma a identidade antes de aprovar.
              </p>
            )}

            <EducandosSection links={data.educandos} onUnlink={(l) => setConfirm({ type: 'unlink', link: l })} />
            <SuggestionsSection suggestions={data.suggestions} linkingId={linkingId} onLink={handleLink} />
            <ParticipantLinkSearch linkedIds={linkedIds} linkingId={linkingId} onLink={handleLink} />
          </div>
        )}
      </div>

      {confirm.type === 'resetLink' && guardian && (
        <ResetLinkDialog
          kind="guardian"
          id={guardian.id}
          name={name}
          onClose={() => setConfirm({ type: 'none' })}
        />
      )}
      {confirm.type === 'deactivate' && (
        <ConfirmDialog
          title="Desativar conta"
          confirmLabel="Desativar"
          isPending={deactivate.isPending}
          onConfirm={handleDeactivate}
          onClose={() => setConfirm({ type: 'none' })}
        >
          {`"${name}" deixa de entrar na app e sai de todas as sessões abertas. As associações mantêm-se e voltam a valer se a conta for reativada.`}
        </ConfirmDialog>
      )}
      {confirm.type === 'delete' && (
        <ConfirmDialog
          title={guardian?.estado === 'Pendente' ? 'Recusar registo' : 'Eliminar conta'}
          confirmLabel={guardian?.estado === 'Pendente' ? 'Recusar' : 'Eliminar'}
          isPending={remove.isPending}
          onConfirm={handleDelete}
          onClose={() => setConfirm({ type: 'none' })}
        >
          {`A conta de "${name}" é eliminada, todas as associações terminam e o nome, o email e o telemóvel são apagados de vez. Esta ação não se pode desfazer.`}
        </ConfirmDialog>
      )}
      {confirm.type === 'unlink' && (
        <ConfirmDialog
          title="Remover associação"
          confirmLabel="Remover"
          isPending={unlink.isPending}
          onConfirm={() => handleUnlink(confirm.link)}
          onClose={() => setConfirm({ type: 'none' })}
        >
          {`"${name}" deixa de ver o percurso, a presença e as credenciais de "${confirm.link.participantName}".`}
        </ConfirmDialog>
      )}
    </div>
  );
}
